"use client";

import { TextAreaField, TextField } from "@/components/tool-kit/primitives";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { NumberFormatter } from "@/lib/tool-kit/stats-tests/format";
import type { ParsedMatrix } from "@/lib/tool-kit/stats-tests-more/matrix";

/*
 * The subjects × conditions editor shared by the repeated-measures tests in this family
 * (Friedman, repeated-measures ANOVA, Cochran's Q): a paste box with one row per subject,
 * an optional list of condition names, and a line saying what was understood. Words arrive
 * through `text`, from the calling widget's strings.
 */

export interface MatrixInputText {
  dataLabel: string;
  dataPlaceholder: string;
  namesLabel: string;
  namesPlaceholder: string;
  /** Plural message: "one: subject|other: subjects". */
  subjectsDetected: string;
  /** Plural message: "one: condition|other: conditions". */
  conditionsDetected: string;
  /** "Line {line}: “{token}” is not a number." */
  errorInvalid: string;
  /** "Line {line} has {got} values, but the first row has {expected}." */
  errorRagged: string;
}

/** The problem with the pasted table, in words, or null. */
export function matrixProblemText(
  parsed: ParsedMatrix,
  text: MatrixInputText,
  fmt: NumberFormatter
): string | null {
  const problem = parsed.problem;
  if (!problem) {
    return null;
  }
  if (problem.kind === "invalid") {
    return t(text.errorInvalid, {
      line: fmt(problem.line),
      token: problem.token,
    });
  }
  return t(text.errorRagged, {
    line: fmt(problem.line),
    got: fmt(problem.got),
    expected: fmt(problem.expected),
  });
}

export function MatrixInput({
  idPrefix,
  text,
  locale,
  fmt,
  parsed,
  data,
  onData,
  names,
  onNames,
}: {
  idPrefix: string;
  text: MatrixInputText;
  locale: string;
  fmt: NumberFormatter;
  parsed: ParsedMatrix;
  data: string;
  onData: (next: string) => void;
  names: string;
  onNames: (next: string) => void;
}) {
  const subjects = parsed.rows.length;
  const hint =
    `${fmt(subjects)}${tPlural(text.subjectsDetected, locale, subjects)}` +
    ` · ${fmt(parsed.width)}${tPlural(text.conditionsDetected, locale, parsed.width)}`;
  return (
    <div className="space-y-4">
      <TextAreaField
        hint={hint}
        id={`${idPrefix}-data`}
        label={text.dataLabel}
        onChange={onData}
        placeholder={text.dataPlaceholder}
        rows={8}
        value={data}
      />
      <TextField
        id={`${idPrefix}-names`}
        label={text.namesLabel}
        onChange={onNames}
        placeholder={text.namesPlaceholder}
        value={names}
      />
    </div>
  );
}
